import html2canvas from "html2canvas";
import {
  hasUserContent,
  hasTaskContent,
  hasNoteContent,
  getTextStyle,
  measureHtmlHeight,
  estimateKonvaTextHeight,
  cleanHtmlContent,
  isContentEmpty,
  calculateGroupHeight,
} from "../../pages/SectionElements/plugins/utils";

// Helper function to convert hex color to RGB
export const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace("#", "");

  // Parse the hex values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  return `${r}, ${g}, ${b}`;
};

// Helper function to convert decimal color to hex
export const decimalToHex = (decimal) => {
  if (typeof decimal === "string" && decimal.startsWith("#")) {
    return decimal; // Already hex
  }
  if (typeof decimal === "number") {
    return `#${decimal.toString(16).padStart(6, "0")}`;
  }
  return decimal || "#000000"; // Default fallback
};

// Calculate total content height using the EXACT same logic as TextElement.jsx's calculateTotalContentHeight
export const calculateTotalContentHeight = (text, canvasWidth = 10000) => {
  let totalHeight = 10; // Initial padding (matches TextElement.jsx line 146)
  const textBlockWidth = text.width || 200;

  // Add content section height
  if (hasUserContent(text)) {
    const contentFontSize =
      text.fontSize || getTextStyle(text, "content").fontSize || 14;
    const contentFontFamily = "Roboto";
    const rawContent = text.content || "";
    const safeContent = rawContent
      .replace(/<div>/g, "<br>")
      .replace(/<\/div>/g, "")
      .replace(/^<br>/, "")
      .replace(/(<br>\s*){2,}/g, "<br>")
      .trim();

    // Calculate actual available width considering canvas boundary
    // MUST MATCH TextElement.jsx lines 109-112 EXACTLY
    // ✅ Use integer width for consistency
    const actualContentWidth = Math.floor(
      Math.min(textBlockWidth - 20, canvasWidth - text.x - 10)
    );

    // ✅ CRITICAL: Process content for highlighting BEFORE measuring to match rendering
    const processedContent = processHtmlContentForHighlighting(safeContent);

    const renderedContentHeight = measureHtmlHeight(
      processedContent,
      actualContentWidth,
      contentFontSize,
      contentFontFamily
    );
    totalHeight += renderedContentHeight;
  }

  // Add parts by type section height
  if (
    text.partsByType &&
    Object.keys(text.partsByType || {}).some(
      (key) => key && text.partsByType[key]?.parts?.length > 0
    )
  ) {
    Object.entries(text.partsByType)
      .sort((a, b) => (a[1].order || 9999) - (b[1].order || 9999))
      .forEach(([typeName, typeData]) => {
        if (!typeData.parts || typeData.parts.length === 0) return;

        const typeFontSize =
          text.fontSize || text.textStyles?.type?.fontSize || 16;
        const typeFontFamily = "Roboto";
        const typeTextHeight = estimateKonvaTextHeight(
          typeName,
          typeFontSize,
          typeFontFamily,
          textBlockWidth - 20
        );
        // Adjust spacing based on font size: small font needs more spacing
        const spacingReduction =
          text.fontSize > 14 ? 16 : text.fontSize === 11 ? 2 : 5;
        totalHeight += typeTextHeight - spacingReduction;

        // Filter out null entries before processing
        typeData.parts.filter(part => part !== null && part !== undefined).forEach((partData) => {
          if (!partData) return; // Additional safety check
          const { part, qty, unit, itemCodeNote, note } = partData;
          const partFontSize =
            text.fontSize || text.textStyles?.part?.fontSize || 14;
          const partFontFamily = "Roboto";
          const content = `${part || ""}${
            qty && parseInt(qty) > 0
              ? ` - Qty-${qty} ${unit || ""}`
              : ` ${unit || ""}`
          }`;

          const safeContent = content
            .replace(/<div>/g, "<br>")
            .replace(/<\/div>/g, "")
            .replace(/^<br>/, "")
            .replace(/(<br>\s*){2,}/g, "<br>")
            .trim();

          // Calculate actual available width for parts considering canvas boundary
          // MUST MATCH TextElement.jsx lines 164-167 EXACTLY
          // ✅ Use integer width for consistency
          const actualPartWidth = Math.floor(
            Math.min(textBlockWidth - 10, canvasWidth - text.x - 20)
          );

          // ✅ CRITICAL: Process content for highlighting BEFORE measuring to match rendering
          const processedPartContent = processHtmlContentForHighlighting(
            safeContent,
            false // underlineLinks = false for parts
          );

          const partTextHeight = measureHtmlHeight(
            processedPartContent,
            actualPartWidth,
            partFontSize,
            partFontFamily
          );
          totalHeight += partTextHeight;

          // Helper function to calculate note height
          const calculateNoteHeight = (noteContent) => {
            if (!noteContent) return 0;
            const safeNoteContent = noteContent
              .replace(/<div>/g, "<br>")
              .replace(/<\/div>/g, "")
              .replace(/^<br>/, "")
              .replace(/(<br>\s*){2,}/g, "<br>")
              .trim();

            const noteFontSize =
              text.fontSize || getTextStyle(text, "note").fontSize || 14;
            const noteFontFamily = "Roboto";

            // Calculate actual available width for part notes considering canvas boundary
            // MUST MATCH TextElement.jsx lines 191-194 EXACTLY
            // ✅ Use integer width for consistency
            const actualPartNoteWidth = Math.floor(
              Math.min(textBlockWidth - 10, canvasWidth - text.x - 40)
            );

            return measureHtmlHeight(
              safeNoteContent,
              actualPartNoteWidth,
              noteFontSize,
              noteFontFamily
            );
          };

          // Add itemCodeNote height first (system-generated note from Item Code Table)
          if (itemCodeNote) {
            totalHeight += calculateNoteHeight(itemCodeNote);
          }

          // Add user's note height (if present)
          if (note) {
            totalHeight += calculateNoteHeight(note);
          }
        });
      });
  }

  // Add task section height
  if (hasTaskContent(text)) {
    const taskFontSize =
      text.fontSize || getTextStyle(text, "task").fontSize || 14;
    const taskFontFamily = getTextStyle(text, "task").fontFamily || "Roboto";
    // Conditional spacing based on font size to maintain visual consistency
    const taskLabelSpace =
      text.fontSize === 11
        ? 15
        : text.fontSize === 14 || text.fontSize === "14"
        ? 20
        : 25;
    totalHeight += taskLabelSpace; // Task label space

    const safeTaskContent = text.task
      .replace(/<div>/g, "<br>")
      .replace(/<\/div>/g, "")
      .replace(/^<br>/, "")
      .replace(/(<br>\s*){2,}/g, "<br>")
      .trim();

    // Calculate actual available width for task considering canvas boundary
    // MUST MATCH TextElement.jsx lines 223-226 EXACTLY
    // ✅ Use integer width for consistency
    const actualTaskWidth = Math.floor(
      Math.min(textBlockWidth - 20, canvasWidth - text.x - 30)
    );

    // ✅ CRITICAL: Process content for highlighting BEFORE measuring to match rendering
    const processedTaskContent = processHtmlContentForHighlighting(
      safeTaskContent,
      true // underlineLinks = true for task content
    );

    const renderedTaskHeight = measureHtmlHeight(
      processedTaskContent,
      actualTaskWidth,
      taskFontSize,
      taskFontFamily
    );
    totalHeight += renderedTaskHeight;
  }

  // Add note section height
  if (hasNoteContent(text)) {
    const noteFontSize =
      text.fontSize || getTextStyle(text, "note").fontSize || 14;
    const noteFontFamily = getTextStyle(text, "note").fontFamily || "Roboto";
    // Conditional spacing based on font size to maintain visual consistency
    const noteLabelSpace =
      text.fontSize === 11
        ? 15
        : text.fontSize === 14 || text.fontSize === "14"
        ? 20
        : 25;
    totalHeight += noteLabelSpace; // Note label space

    const safeNoteContent = text.note
      .replace(/<div>/g, "<br>")
      .replace(/<\/div>/g, "")
      .replace(/^<br>/, "")
      .replace(/(<br>\s*){2,}/g, "<br>")
      .trim();

    // Calculate actual available width for note considering canvas boundary
    // MUST MATCH TextElement.jsx lines 252-255 EXACTLY
    // ✅ Use integer width for consistency
    const actualNoteWidth = Math.floor(
      Math.min(textBlockWidth - 20, canvasWidth - text.x - 30)
    );

    // ✅ CRITICAL: Process content for highlighting BEFORE measuring to match rendering
    const processedNoteContent = processHtmlContentForHighlighting(
      safeNoteContent,
      true // underlineLinks = true for note content
    );

    const renderedNoteHeight = measureHtmlHeight(
      processedNoteContent,
      actualNoteWidth,
      noteFontSize,
      noteFontFamily
    );
    totalHeight += renderedNoteHeight;
  }

  // Add bottom spacing for small font size
  const bottomSpacing = text.fontSize === 11 ? 4 : 0;
  return totalHeight + bottomSpacing + 5;
};

// Add this new function for HTML-based snapshot generation
export const generateSnapshotFromPageDataHTML = (pageData) => {
  return new Promise(async (resolve) => {
    try {
      // Create a temporary container
      const tempContainer = document.createElement("div");
      tempContainer.style.cssText = `
        position: absolute;
        left: -9999px;
        width: 700px;
        background: white;
        font-family: Roboto, Arial, sans-serif;
        overflow: hidden;
      `;

      // Set height based on page data
      const pageHeightVh =
        pageData?.layout_config?.layout?.page_height_vh ||
        pageData?.page_height_vh ||
        null;

      if (pageHeightVh && !Number.isNaN(parseInt(pageHeightVh))) {
        tempContainer.style.height = `${Math.round(
          ((parseInt(pageHeightVh) + 10) / 100) * window.innerHeight
        )}px`;
      } else {
        tempContainer.style.height = "861px";
      }

      // Add to DOM temporarily
      document.body.appendChild(tempContainer);

      // Generate HTML content
      const htmlContent = generatePageHTML(pageData);
      tempContainer.innerHTML = htmlContent;

      // Set crossOrigin attribute on all images to enable CORS
      const images = tempContainer.querySelectorAll("img");
      images.forEach((img) => {
        img.crossOrigin = "anonymous";
      });

      // Wait for images to load
      await waitForImages(tempContainer);

      // Convert to canvas using html2canvas
      const canvas = await html2canvas(tempContainer, {
        width: 700,
        height: parseInt(tempContainer.style.height),
        backgroundColor: "#ffffff",
        scale: 1,
        useCORS: true,
        allowTaint: false, // Changed to false for better CORS handling
        logging: false,
      });

      // Clean up
      document.body.removeChild(tempContainer);

      // Return data URL
      resolve(canvas.toDataURL("image/png"));
    } catch (error) {
      // Fallback to original method if HTML method fails
      // Fallback to generateSnapshotFromPageDataHTML for canvas-based generation
      // We use html2canvas directly instead of importing from AddCanvasTemplate
      resolve(""); // Return empty string for now
    }
  });
};

// Helper function to generate HTML content
export const generatePageHTML = (pageData) => {
  let html = "";

  // Add minimal styles for ellipsis text in shapes
  html += `
    <style>
      /* Force all elements inside text containers to be inline for ellipsis to work */
      .rect-text-content *, .circle-text-content * {
        display: inline !important;
        margin: 0 !important;
        padding: 0 !important;
        line-height: inherit !important;
        background: transparent !important;
        position: static !important;
        z-index: auto !important;
      }
      
      /* Exception for line breaks */
      .rect-text-content br, .circle-text-content br {
        display: inline !important;
        content: " " !important;
      }
      
      /* Preserve text highlighting backgrounds */
      .rect-text-content span[style*="background"], 
      .circle-text-content span[style*="background"] {
        background: inherit !important;
      }
    </style>
  `;

  // Add images first (background layer)
  if (Array.isArray(pageData.images)) {
    pageData.images.forEach((image) => {
      if (image.image_path) {
        const { image_properties = {} } = image;
        const {
          x = 0,
          y = 0,
          width = 200,
          height = 200,
          rotation = 0,
          scaleX = 1,
          scaleY = 1,
          flipHorizontal = false,
          flipVertical = false,
        } = image_properties;

        // Calculate transform for rotation and flipping
        // Images are center-positioned with offset, so rotation should be around center
        // Apply flip transforms based on scaleX/scaleY or flip flags
        const transforms = [];
        if (rotation) transforms.push(`rotate(${rotation}deg)`);

        // Apply flip transforms - check both scaleX/scaleY and flip flags
        const effectiveScaleX = scaleX < 0 || flipHorizontal ? -1 : 1;
        const effectiveScaleY = scaleY < 0 || flipVertical ? -1 : 1;

        if (effectiveScaleX !== 1 || effectiveScaleY !== 1) {
          transforms.push(`scale(${effectiveScaleX}, ${effectiveScaleY})`);
        }

        const transform = transforms.length > 0 ? transforms.join(" ") : "";

        html += `
          <img 
            src="${image.image_path}" 
            crossorigin="anonymous"
            style="
              position: absolute;
              left: ${x - width / 2 + 20}px;
              top: ${y - height / 2 + 40}px;
              width: ${width}px;
              height: ${height}px;
              ${transform ? `transform: ${transform};` : ""}
              z-index: 1;
            "
            alt=""
          />
        `;
      }
    });
  }

  // Add shapes
  const shapes = Array.isArray(pageData.shapes) ? pageData.shapes : [];
  shapes.forEach((shape) => {
    const { type: shape_type, shape_properties = {} } = shape;
    const props = shape_properties;

    if (shape_type === "image") return;

    switch (shape_type) {
      case "rectangle":
        html += generateRectangleHTML(props);
        break;
      case "circle":
        html += generateCircleHTML(props);
        break;
      case "text":
        html += generateTextHTML(props);
        break;
      case "lines":
        html += generateLineHTML(props);
        break;
      case "arrow":
      case "double_sided_arrow":
        html += generateArrowHTML(props, shape_type);
        break;
    }
  });

  return html;
};

// Generate HTML for rectangles
export const generateRectangleHTML = (props) => {
  const {
    x = 0,
    y = 0,
    width = 100,
    height = 100,
    text = "",
    fontSize = 16,
    fontStyle = "normal",
    textColor = "#000",
    textDecoration = "",
    align: horizontalAlign = "center",
    verticalAlign = "middle",
    rotation = 0,
    fill = "#ffffff",
    fillColor,
    fillOpacity = 1,
    stroke = "#000000",
    strokeColor,
    strokeOpacity = 1,
    strokeWidth = 2,
  } = props;

  const resolvedFill = fillColor || fill || "#ffffff";
  const resolvedStroke = stroke || strokeColor || "#000000";

  const transform = rotation ? `transform: rotate(${rotation}deg);` : "";
  const textAlign = horizontalAlign || "center";
  const verticalAlignStyle =
    verticalAlign === "top"
      ? "flex-start"
      : verticalAlign === "bottom"
      ? "flex-end"
      : "center";

  // Ensure rectangle is visible even with transparent fill by using rgba
  const fillStyle =
    fillOpacity === 0
      ? "transparent"
      : fillOpacity < 1
      ? `rgba(${hexToRgb(resolvedFill)}, ${fillOpacity})`
      : resolvedFill;

  // Calculate text container dimensions accounting for stroke width and padding
  const strokePadding = Math.ceil(strokeWidth / 2);
  const containerWidth = Math.max(0, width - strokePadding * 2);
  const containerHeight = Math.max(0, height - strokePadding * 2);
  const textContainerWidth = Math.max(0, containerWidth * 0.9); // 90% of available width for less spacing (matches RectangleElement.jsx)
  const textContainerHeight = Math.max(0, containerHeight * 0.9); // 90% of available height for less spacing (matches RectangleElement.jsx)

  // Calculate max lines for -webkit-line-clamp (matches RectangleElement.jsx)
  const maxLines = Math.max(1, Math.floor(textContainerHeight / fontSize));

  // Process highlighting to break into word-level spans
  let processedText = processHtmlContentForHighlighting(text || "", false);

  // Estimate maximum characters that can fit (be conservative)
  const avgCharsPerLine = Math.floor(textContainerWidth / (fontSize * 0.6)); // More conservative estimate
  const maxChars = Math.floor(maxLines * avgCharsPerLine * 0.85); // Reduce by 15% to be safe

  // Check if text needs truncation
  if (typeof document !== "undefined") {
    const temp = document.createElement("div");
    temp.innerHTML = processedText;
    const plainText = temp.textContent || temp.innerText || "";

    // If text exceeds estimated max, truncate it
    if (plainText.length > maxChars) {
      const truncatedLength = Math.max(0, maxChars - 3); // Reserve space for "..."
      let currentLength = 0;
      let truncated = false;

      const truncateNode = (node) => {
        if (truncated) return null;

        if (node.nodeType === Node.TEXT_NODE) {
          const textLength = node.textContent.length;
          if (currentLength + textLength <= truncatedLength) {
            currentLength += textLength;
            return node.cloneNode(true);
          } else {
            truncated = true;
            const remaining = Math.max(0, truncatedLength - currentLength);
            if (remaining > 0) {
              return document.createTextNode(
                node.textContent.substring(0, remaining) + "..."
              );
            } else {
              return document.createTextNode("...");
            }
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const cloned = node.cloneNode(false);
          for (let child of node.childNodes) {
            const truncatedChild = truncateNode(child);
            if (truncatedChild) {
              cloned.appendChild(truncatedChild);
            }
            if (truncated) break;
          }
          return cloned;
        }
        return null;
      };

      const result = document.createElement("div");
      for (let child of temp.childNodes) {
        const truncatedChild = truncateNode(child);
        if (truncatedChild) {
          result.appendChild(truncatedChild);
        }
        if (truncated) break;
      }

      processedText = result.innerHTML;
    }
  }

  // Calculate the padding offset to center the text container (10% on each side = 20% total padding)
  const textContainerOffsetX = (containerWidth - textContainerWidth) / 2;
  const textContainerOffsetY = (containerHeight - textContainerHeight) / 2;

  return `
    <div style="
      position: absolute;
      left: ${x + 20}px;
      top: ${y + 40}px;
      width: ${width}px;
      height: ${height}px;
      background-color: ${fillStyle};
      border: ${strokeWidth}px solid ${resolvedStroke};
      ${transform}
      z-index: 2;
      overflow: hidden;
    ">
      ${
        text
          ? `
        <div style="
          position: absolute;
          left: ${strokePadding + textContainerOffsetX}px;
          top: ${strokePadding + textContainerOffsetY}px;
          width: ${textContainerWidth}px;
          height: ${textContainerHeight}px;
          display: flex;
          align-items: ${verticalAlignStyle};
          justify-content: ${
            textAlign === "left"
              ? "flex-start"
              : textAlign === "right"
              ? "flex-end"
              : "center"
          };
          padding: 0px;
          box-sizing: border-box;
          overflow: hidden;
        ">
          <div class="rect-text-content" style="
            font-family: Roboto;
            font-size: ${fontSize}px;
            font-weight: ${fontStyle.includes("bold") ? "bold" : "normal"};
            font-style: ${fontStyle.includes("italic") ? "italic" : "normal"};
            color: ${textColor};
            text-decoration: ${textDecoration};
            text-align: ${textAlign};
            line-height: 1em;
            word-wrap: break-word;
            word-break: break-word;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: ${maxLines};
            -webkit-box-orient: vertical;
            max-width: ${textContainerWidth}px;
            max-height: ${maxLines * fontSize}px;
            margin: 0;
            padding: 0;
            pointer-events: none;
            user-select: none;
            white-space: normal;
          ">${processedText}</div>
        </div>
      `
          : ""
      }
    </div>
  `;
};

// Generate HTML for circles and ellipses
export const generateCircleHTML = (props) => {
  const {
    x = 0,
    y = 0,
    radius = 50,
    radiusX,
    radiusY,
    maintainAspectRatio = true,
    text = "",
    fontSize = 16,
    fontStyle = "normal",
    textColor = "#000",
    textDecoration = "",
    align: horizontalAlign = "center",
    verticalAlign = "middle",
    rotation = 0,
    fill = "#ffffff",
    fillColor,
    fillOpacity = 1,
    stroke = "#000000",
    strokeColor,
    strokeOpacity = 1,
    strokeWidth = 1,
  } = props;

  const resolvedFill = fillColor || fill || "#ffffff";
  const resolvedStroke = stroke || strokeColor || "#000000";

  // Calculate actual dimensions based on maintainAspectRatio (same logic as CircleElement)
  const actualRadiusX = maintainAspectRatio ? radius : radiusX || radius;
  const actualRadiusY = maintainAspectRatio ? radius : radiusY || radius;

  const transform = rotation ? `transform: rotate(${rotation}deg);` : "";
  const textAlign = horizontalAlign || "center";
  const verticalAlignStyle =
    verticalAlign === "top"
      ? "flex-start"
      : verticalAlign === "bottom"
      ? "flex-end"
      : "center";

  // Ensure circle/ellipse is visible even with transparent fill by using rgba
  const fillStyle =
    fillOpacity === 0
      ? "transparent"
      : fillOpacity < 1
      ? `rgba(${hexToRgb(resolvedFill)}, ${fillOpacity})`
      : resolvedFill;

  // Calculate text container dimensions accounting for stroke width (matches CircleElement.jsx)
  const strokePadding = Math.ceil(strokeWidth / 2);
  const availableWidth = Math.max(0, actualRadiusX * 2 - strokePadding * 2);
  const availableHeight = Math.max(0, actualRadiusY * 2 - strokePadding * 2);
  const availableSize = Math.min(availableWidth, availableHeight);
  const textContainerSize = Math.max(0, availableSize * 0.85); // Increased from 0.707 for less spacing (matches CircleElement.jsx)

  // Calculate max lines for -webkit-line-clamp (matches RectangleElement.jsx)
  const maxLines = Math.max(1, Math.floor(textContainerSize / fontSize));

  // Process highlighting to break into word-level spans
  let processedText = processHtmlContentForHighlighting(text || "", false);

  // Estimate maximum characters that can fit (be conservative)
  const avgCharsPerLine = Math.floor(textContainerSize / (fontSize * 0.6)); // More conservative estimate
  const maxChars = Math.floor(maxLines * avgCharsPerLine * 0.85); // Reduce by 15% to be safe

  // Check if text needs truncation
  if (typeof document !== "undefined") {
    const temp = document.createElement("div");
    temp.innerHTML = processedText;
    const plainText = temp.textContent || temp.innerText || "";

    // If text exceeds estimated max, truncate it
    if (plainText.length > maxChars) {
      const truncatedLength = Math.max(0, maxChars - 3); // Reserve space for "..."
      let currentLength = 0;
      let truncated = false;

      const truncateNode = (node) => {
        if (truncated) return null;

        if (node.nodeType === Node.TEXT_NODE) {
          const textLength = node.textContent.length;
          if (currentLength + textLength <= truncatedLength) {
            currentLength += textLength;
            return node.cloneNode(true);
          } else {
            truncated = true;
            const remaining = Math.max(0, truncatedLength - currentLength);
            if (remaining > 0) {
              return document.createTextNode(
                node.textContent.substring(0, remaining) + "..."
              );
            } else {
              return document.createTextNode("...");
            }
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const cloned = node.cloneNode(false);
          for (let child of node.childNodes) {
            const truncatedChild = truncateNode(child);
            if (truncatedChild) {
              cloned.appendChild(truncatedChild);
            }
            if (truncated) break;
          }
          return cloned;
        }
        return null;
      };

      const result = document.createElement("div");
      for (let child of temp.childNodes) {
        const truncatedChild = truncateNode(child);
        if (truncatedChild) {
          result.appendChild(truncatedChild);
        }
        if (truncated) break;
      }

      processedText = result.innerHTML;
    }
  }

  // Calculate the offset to center the text container within the circle/ellipse
  // The text container is an inscribed square, so we need to center it within the circle
  const textContainerOffsetX = actualRadiusX - textContainerSize / 2;
  const textContainerOffsetY = actualRadiusY - textContainerSize / 2;

  return `
    <div style="
      position: absolute;
      left: ${x - actualRadiusX + 20}px;
      top: ${y - actualRadiusY + 40}px;
      width: ${actualRadiusX * 2}px;
      height: ${actualRadiusY * 2}px;
      background-color: ${fillStyle};
      border: ${strokeWidth}px solid ${resolvedStroke};
      border-radius: 50%;
      ${transform}
      z-index: 2;
      overflow: hidden;
    ">
      ${
        text
          ? `
        <div style="
          position: absolute;
          left: ${textContainerOffsetX}px;
          top: ${textContainerOffsetY}px;
          width: ${textContainerSize}px;
          height: ${textContainerSize}px;
          display: flex;
          align-items: ${verticalAlignStyle};
          justify-content: ${
            textAlign === "left"
              ? "flex-start"
              : textAlign === "right"
              ? "flex-end"
              : "center"
          };
          padding: 0px;
          box-sizing: border-box;
          overflow: hidden;
        ">
          <div class="circle-text-content" style="
            font-family: Roboto;
            font-size: ${fontSize}px;
            font-weight: ${fontStyle.includes("bold") ? "bold" : "normal"};
            font-style: ${fontStyle.includes("italic") ? "italic" : "normal"};
            color: ${textColor};
            text-decoration: ${textDecoration};
            text-align: ${textAlign};
            line-height: 1em;
            word-wrap: break-word;
            word-break: break-word;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: ${maxLines};
            -webkit-box-orient: vertical;
            max-width: ${textContainerSize}px;
            max-height: ${maxLines * fontSize}px;
            margin: 0;
            padding: 0;
            pointer-events: none;
            user-select: none;
            white-space: normal;
          ">${processedText}</div>
        </div>
      `
          : ""
      }
    </div>
  `;
};

// Generate HTML for text elements (the main one you're having issues with)
export const generateTextHTML = (props) => {
  const {
    x = 0,
    y = 0,
    width = 200,
    height = 300, // This will be overridden with calculateGroupHeight
    content = "",
    note = "",
    task = "",
    showBackground = true,
    fontSize = 14,
    fontFamily = "Roboto",
    fillColor = "#000",
    textStyles = {},
    partsByType = {},
    rotation = 0,
    taskPlainText = "",
    notePlainText = "",
    plainText = "",
    backgroundColor = "#FFFFFF",
  } = props;

  // Conditional minimum height based on font size (matches TextElement.jsx)
  const getMinHeight = () => {
    if (fontSize === 11) return 34; // Small font - increased bottom spacing
    if (fontSize === 14 || fontSize === "14") return 35; // Medium font - more reduction
    return 43; // Big font
  };

  // Use the EXACT same height calculation as TextElement.jsx
  const calculatedHeight = Math.max(
    calculateTotalContentHeight(props),
    getMinHeight()
  );

  const transform = rotation ? `transform: rotate(${rotation}deg);` : "";

  // Handle background color logic based on showBackground property
  let resolvedBackgroundColor;
  let zIndexValue;

  if (showBackground === true) {
    // If showBackground is true, use backgroundColor if provided, otherwise use white
    // Make it non-transparent and opaque with highest z-index
    resolvedBackgroundColor = backgroundColor || "#FFFFFF";
    zIndexValue = 9999; // Highest z-index to ensure no elements show behind
  } else {
    // If showBackground is false, use transparent
    resolvedBackgroundColor = "transparent";
    zIndexValue = 2; // Normal z-index
  }

  return `
    <div style="
      position: absolute;
      left: ${x + 20}px;
      top: ${y + 40}px;
      width: ${width}px;
      height: ${calculatedHeight}px;
      border: 2px solid #CCCCCC;
      background-color: ${resolvedBackgroundColor};
      padding: 0;
      margin: 0;
      box-sizing: border-box;
      font-family: ${fontFamily}, Arial, sans-serif;
      font-size: ${fontSize}px;
      color: ${fillColor};
      ${transform}
      z-index: ${zIndexValue};
      line-height: 1.4;
      overflow: hidden;
      text-rendering: geometricPrecision;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      font-kerning: normal;
      font-variant-ligatures: none;
      letter-spacing: normal;
      word-spacing: normal;
      white-space: pre-wrap;
      word-break: break-word;
      overflow-wrap: break-word;
    ">
      <style>
        /* MUST MATCH utils.js measureHtmlHeight styles EXACTLY */
        /* Paragraph styles */
        p {
          margin: 0px 1px;
          padding: 0px;
        }
        
        /* List styles - MUST match measureHtmlHeight */
        ul, ol {
          margin: 0;
          padding: 0;
          list-style-position: outside;
          padding-left: 20px;
        }
        
        /* Explicitly set list-style-type for bullets and numbering */
        ul {
          list-style-type: disc;
        }
        
        ol {
          list-style-type: decimal;
        }
        
        li {
          display: list-item;
          margin: 0;
          padding: 0;
          line-height: 1.4;
        }
        /* spacing only between items, not after the last one */
        li + li { margin-top: 2px; }
        
        /* Heading styles */
        h1, h2, h3, h4, h5, h6 {
          margin: 0;
          padding: 0;
          font-weight: inherit;
          font-size: inherit;
        }
        
        /* Other common elements */
        div, span, strong, em, i, b {
          margin: 0;
          padding: 0;
        }
        
        /* Block elements */
        blockquote, pre, code {
          margin: 0;
          padding: 0;
        }
        
        /* Table elements */
        table, tr, td, th {
          margin: 0;
          padding: 0;
          border-collapse: collapse;
        }
        
        /* Form elements */
        input, textarea, select, button {
          margin: 0;
          padding: 0;
          font-family: inherit;
          font-size: inherit;
        }
        
        /* Ensure no trailing margin/padding contributes to bottom height */
        *:last-child {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
        
        /* Link styles - ensure underlines work properly across wrapped text */
        /* Apply to all links and their children */
        a[href], a[href] * {
          text-decoration: underline !important;
          text-decoration-color: #4da6ff !important;
          text-decoration-skip-ink: none;
        }
        
        /* Exception: Remove underlines from links in parts */
        [data-content-type="part"] a[href],
        [data-content-type="part"] a[href] * {
          text-decoration: none !important;
        }
        
        /* Ensure text in highlighted spans is always visible */
        span[style*="background"] {
          display: inline !important;
          position: relative !important;
          z-index: 1 !important;
          box-decoration-break: clone !important;
          -webkit-box-decoration-break: clone !important;
        }

        /* Preserve text color inside highlighted spans */
        span[style*="background"] span[style*="color"],
        span[style*="background"] strong,
        span[style*="background"] a {
          color: inherit !important;
          display: inline !important;
          position: relative !important;
          z-index: 2 !important;
        }

        /* Ensure highlighted whitespace spans maintain their background */
        span[style*="background"][style*="padding: 0px"] {
          background-color: inherit !important;
        }
        
        /* Force visibility and proper rendering */
        .highlighted-text * {
          visibility: visible !important;
          opacity: 1 !important;
        }
        
        .highlighted-text p {
          display: block !important;
        }
        
        .highlighted-text span {
          display: inline !important;
        }
        
        /* Exception for lists - must use proper display values */
        .highlighted-text ul,
        .highlighted-text ol {
          display: block !important;
          list-style-type: disc !important;
        }
        
        .highlighted-text ol {
          list-style-type: decimal !important;
        }
        
        .highlighted-text li {
          display: list-item !important;
        }
        
        /* Ensure strong tags are visible */
        strong {
          font-weight: bold !important;
          display: inline !important;
          position: relative !important;
          z-index: 2 !important;
        }
        
        /* Prevent any clipping or overflow issues */
        span, strong, a {
          overflow: visible !important;
          text-overflow: clip !important;
        }
        
        /* Ensure highlighted text has proper text rendering */
        span[style*="background"] * {
          text-rendering: geometricPrecision !important;
          -webkit-font-smoothing: antialiased !important;
        }
      </style>
      <div class="highlighted-text">
${generateTextContentHTML(props)}
      </div>
    </div>
  `;
};

// Generate the actual text content HTML - Complete TextElement structure
export const generateTextContentHTML = (props) => {
  const {
    content,
    note,
    task,
    partsByType,
    fontSize = 14,
    textStyles = {},
    taskPlainText,
    notePlainText,
    plainText,
    width = 200,
    x = 0, // Add x position for consistent width calculations
  } = props;

  let html = "";
  let currentYOffset = 10; // Initial padding from the top of the group's content area (matches TextElement.jsx line 140)
  const textBlockWidth = width;
  // Use a large canvas width for snapshots (no boundary restrictions)
  const canvasWidth = 10000;

  // Content Section - exactly like TextElement
  if (hasUserContent(props)) {
    const contentFontSize =
      fontSize || getTextStyle(props, "content").fontSize || 14;
    const contentFontFamily = "Roboto";

    const rawContent = content || "";
    const yContentBody = currentYOffset; // Use currentYOffset directly (matches TextElement.jsx)

    // Process content EXACTLY like TextElement.jsx (lines 800-805) before processing highlighting
    const safeContent = rawContent
      .replace(/<div>/g, "<br>")
      .replace(/<\/div>/g, "")
      .replace(/^<br>/, "")
      .replace(/(<br>\s*){2,}/g, "<br>")
      .trim();

    // Calculate actual available width - MUST MATCH TextElement.jsx lines 161-165
    // ✅ Use floored width for BOTH measurement and rendering to ensure consistency
    const actualContentWidth = Math.floor(
      Math.min(textBlockWidth - 20, canvasWidth - x - 10)
    );

    // ✅ Process HTML to fix multiline highlighting
    const processedContent = processHtmlContentForHighlighting(safeContent);

    // ✅ CRITICAL: Measure the PROCESSED content using the SAME floored width as rendering
    const renderedContentHeight = measureHtmlHeight(
      processedContent,
      actualContentWidth,
      contentFontSize,
      contentFontFamily
    );

    // Update currentYOffset AFTER calculating height
    currentYOffset += renderedContentHeight; // Add consistent spacing

    html += `
      <div style="
        position: absolute;
        top: ${yContentBody}px;
        left: 10px;
        width: ${actualContentWidth}px;
        max-width: ${actualContentWidth}px;
        font-size: ${contentFontSize}px;
        font-family: ${contentFontFamily};
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: break-word;
        text-decoration: none;
        text-rendering: geometricPrecision;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-kerning: normal;
        font-variant-ligatures: none;
        letter-spacing: normal;
        word-spacing: normal;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        display: block;
      ">${processedContent || ""}</div>
    `;
  }

  // PartsByType Section - exactly like TextElement
  if (
    partsByType &&
    Object.keys(partsByType || {}).some(
      (key) => key && partsByType[key]?.parts?.length > 0
    )
  ) {
    const partGroups = [];

    Object.entries(partsByType)
      .sort((a, b) => (a[1].order || 9999) - (b[1].order || 9999))
      .forEach(([typeName, typeData]) => {
        if (!typeData.parts || typeData.parts.length === 0) return;

        const typeFontSize = fontSize || textStyles?.type?.fontSize || 16;
        const typeFontFamily = "Roboto";

        const typeTextHeight = estimateKonvaTextHeight(
          typeName,
          typeFontSize,
          typeFontFamily,
          textBlockWidth - 20
        );

        const typeY = currentYOffset;

        // Type name
        html += `
          <div style="
            position: absolute;
            top: ${typeY}px;
            left: 10px;
            width: ${textBlockWidth - 20}px;
            font-family: ${typeFontFamily};
            font-size: ${typeFontSize}px;
            font-weight: bold;
            color: ${textStyles?.type?.color || "black"};
            overflow: hidden;
            word-wrap: break-word;
          ">${typeName}</div>
        `;

        // Adjust spacing based on font size: small font needs more spacing
        const spacingReduction = fontSize > 14 ? 16 : fontSize === 11 ? 2 : 5;
        currentYOffset += typeTextHeight - spacingReduction;

        // Filter out null entries before sorting
        const sortedParts = typeData.parts
          .filter(part => part !== null && part !== undefined)
          .slice()
          .sort((a, b) => {
            if (!a || !b) return 0; // Handle null entries
            const partA = a.part?.match(/\(([^)]+)\)/)?.[1] || "";
            const partB = b.part?.match(/\(([^)]+)\)/)?.[1] || "";
            return partA.localeCompare(partB, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          });

        sortedParts.forEach(
          (partData, index) => {
            // Skip null or undefined entries
            if (!partData) return;
            
            const {
              id,
              part,
              qty,
              unit,
              highlighted,
              note: partNote,
              noteIsHtml,
              noteListType,
            } = partData;
            const partFontSize = fontSize || textStyles?.part?.fontSize || 14;
            const partFontFamily = "Roboto";
            const content = `${part || ""}${
              qty && parseInt(qty) > 0
                ? ` - Qty-${qty} ${unit || ""}`
                : ` ${unit || ""}`
            }`;

            // Process part content EXACTLY like TextElement.jsx (lines 1193-1198)
            const safeContent = content
              .replace(/<div>/g, "<br>")
              .replace(/<\/div>/g, "")
              .replace(/^<br>/, "")
              .replace(/(<br>\s*){2,}/g, "<br>")
              .trim();

            // Calculate actual available width - MUST MATCH TextElement.jsx lines 218-222
            // ✅ Use floored width for BOTH measurement and rendering to ensure consistency
            const actualPartWidth = Math.floor(
              Math.min(textBlockWidth - 10, canvasWidth - x - 20)
            );

            // ✅ Process HTML to fix multiline highlighting (no underline for parts)
            const processedPartContent = processHtmlContentForHighlighting(
              safeContent,
              false // underlineLinks = false for parts
            );

            // ✅ CRITICAL: Measure the PROCESSED content using the SAME floored width as rendering
            const partTextHeight = measureHtmlHeight(
              processedPartContent,
              actualPartWidth,
              partFontSize,
              partFontFamily
            );

            const partY = currentYOffset;

            // Check if part is highlighted and apply yellow background
            const isHighlighted = highlighted;
            const highlightStyle = isHighlighted
              ? "background-color: yellow; padding: 2px 4px; border-radius: 3px;"
              : "";

            html += `
              <div data-content-type="part" style="
                position: absolute;
                top: ${partY}px;
                left: 10px;
                width: ${actualPartWidth}px;
                max-width: ${actualPartWidth}px;
                font-size: ${partFontSize}px;
                font-family: ${partFontFamily};
                line-height: 1.4;
                white-space: normal;
                word-break: break-word;
                overflow-wrap: break-word;
                color: ${textStyles?.part?.color || "#000"};
                text-decoration: none;
                text-rendering: geometricPrecision;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                font-kerning: normal;
                font-variant-ligatures: none;
                letter-spacing: normal;
                word-spacing: normal;
                box-sizing: border-box;
                margin: 0;
                padding: 0;
                display: block;
                ${highlightStyle}
              ">${processedPartContent}</div>
            `;

            currentYOffset += partTextHeight; // Consistent spacing after parts

            // Handle note for this part
            if (partNote) {
              const safeNoteContent = partNote
                .replace(/<div>/g, "<br>")
                .replace(/<\/div>/g, "")
                .replace(/^<br>/, "")
                .replace(/(<br>\s*){2,}/g, "<br>")
                .trim();

              const noteFontSize =
                fontSize || getTextStyle(props, "note").fontSize || 14;
              const noteFontFamily = "Roboto";

              // Calculate actual available width for part notes - MUST MATCH TextElement.jsx lines 245-249
              // ✅ Use floored width for BOTH measurement and rendering to ensure consistency
              const actualPartNoteWidth = Math.floor(
                Math.min(textBlockWidth - 10, canvasWidth - x - 40)
              );

              // ✅ CRITICAL: Measure using the SAME floored width as rendering
              const noteHeight = measureHtmlHeight(
                safeNoteContent,
                actualPartNoteWidth,
                noteFontSize,
                noteFontFamily
              );

              const noteY = currentYOffset;

              html += `
                <div style="
                  position: absolute;
                  top: ${noteY}px;
                  left: 10px;
                  width: ${actualPartNoteWidth}px;
                  max-width: ${actualPartNoteWidth}px;
                  font-size: ${noteFontSize}px;
                  font-family: ${noteFontFamily};
                  line-height: 1.4;
                  white-space: normal;
                  word-break: break-word;
                  overflow-wrap: break-word;
                  color: ${getTextStyle(props, "note").color || "#000"};
                  text-decoration: none;
                  text-rendering: geometricPrecision;
                  -webkit-font-smoothing: antialiased;
                  -moz-osx-font-smoothing: grayscale;
                  font-kerning: normal;
                  font-variant-ligatures: none;
                  letter-spacing: normal;
                  word-spacing: normal;
                  box-sizing: border-box;
                  margin: 0;
                  padding: 0;
                  display: block;
                ">${safeNoteContent}</div>
              `;

              currentYOffset += noteHeight; // Consistent spacing after notes
            }
          }
        );

        // No spacing between different part types (matches TextElement.jsx)
      });
  }

  // Task Section - exactly like TextElement
  if (hasTaskContent(props)) {
    const taskFontSize = fontSize || getTextStyle(props, "task").fontSize || 14;
    const taskFontFamily = getTextStyle(props, "task").fontFamily || "Roboto";

    const yTaskLabel = currentYOffset;
    // Conditional spacing based on font size: Small (11) = 15px, Medium (14) = 20px, Big (18) = 25px
    const taskLabelSpace =
      fontSize === 11 ? 15 : fontSize === 14 || fontSize === "14" ? 20 : 25;
    currentYOffset += taskLabelSpace; // Space between "Task" heading and task content

    const yTaskContent = currentYOffset;

    // Process task content EXACTLY like TextElement.jsx (lines 1726-1731)
    const safeTaskContent = task
      .replace(/<div>/g, "<br>")
      .replace(/<\/div>/g, "")
      .replace(/^<br>/, "")
      .replace(/(<br>\s*){2,}/g, "<br>")
      .trim();

    // Calculate actual available width - MUST MATCH TextElement.jsx lines 1763-1767
    // ✅ Use floored width for BOTH measurement and rendering to ensure consistency
    const actualTaskWidth = Math.floor(
      Math.min(textBlockWidth - 20, canvasWidth - x - 30)
    );

    // ✅ Process HTML to fix multiline highlighting (same as PDF export in MyCanvas.js lines 8578-8582)
    const processedTaskContent = processHtmlContentForHighlighting(
      safeTaskContent,
      true // underlineLinks = true for task content
    );

    // ✅ CRITICAL: Measure the PROCESSED content using the SAME floored width as rendering
    const renderedTaskHeight = measureHtmlHeight(
      processedTaskContent,
      actualTaskWidth,
      taskFontSize,
      taskFontFamily
    );

    currentYOffset += renderedTaskHeight; // Update offset after task content

    // Task heading
    html += `
      <div style="
        position: absolute;
        top: ${yTaskLabel}px;
        left: 10px;
        width: ${actualTaskWidth}px;
        font-family: Roboto;
        font-size: ${fontSize || 18}px;
        font-weight: bold;
        color: black;
      ">Task</div>
    `;

    // Task content - render with PROCESSED content (highlighting fixed) with natural height
    html += `
      <div style="
        position: absolute;
        top: ${yTaskContent}px;
        left: 10px;
        width: ${actualTaskWidth}px;
        max-width: ${actualTaskWidth}px;
        font-size: ${taskFontSize}px;
        font-family: ${taskFontFamily};
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: break-word;
        color: ${getTextStyle(props, "task").color || "#000"};
        text-decoration: none;
        text-rendering: geometricPrecision;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-kerning: normal;
        font-variant-ligatures: none;
        letter-spacing: normal;
        word-spacing: normal;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        display: block;
      ">${processedTaskContent || ""}</div>
    `;
  }

  // Note Section - exactly like TextElement
  if (hasNoteContent(props)) {
    const noteFontSize = fontSize || getTextStyle(props, "note").fontSize || 14;
    const noteFontFamily = getTextStyle(props, "note").fontFamily || "Roboto";

    const yNoteLabel = currentYOffset;
    // Conditional spacing based on font size: Small (11) = 15px, Medium (14) = 20px, Big (18) = 25px
    const noteLabelSpace =
      fontSize === 11 ? 15 : fontSize === 14 || fontSize === "14" ? 20 : 25;
    currentYOffset += noteLabelSpace; // Space between "Note" heading and note content

    const yNoteContent = currentYOffset;

    // Process note content EXACTLY like TextElement.jsx (lines 2050-2055)
    const safeNoteContent = note
      .replace(/<div>/g, "<br>")
      .replace(/<\/div>/g, "")
      .replace(/^<br>/, "")
      .replace(/(<br>\s*){2,}/g, "<br>")
      .trim();

    // Calculate actual available width - MUST MATCH TextElement.jsx lines 2069-2072
    // ✅ Use floored width for BOTH measurement and rendering to ensure consistency
    const actualNoteWidth = Math.floor(
      Math.min(textBlockWidth - 20, canvasWidth - x - 30)
    );

    // ✅ Process HTML to fix multiline highlighting (same as PDF export in MyCanvas.js lines 8578-8582)
    const processedNoteContent = processHtmlContentForHighlighting(
      safeNoteContent,
      true // underlineLinks = true for note content
    );

    // ✅ CRITICAL: Measure the PROCESSED content using the SAME floored width as rendering
    const renderedNoteHeight = measureHtmlHeight(
      processedNoteContent,
      actualNoteWidth,
      noteFontSize,
      noteFontFamily
    );

    currentYOffset += renderedNoteHeight; // Update offset after note content

    // Note heading
    html += `
      <div style="
        position: absolute;
        top: ${yNoteLabel}px;
        left: 10px;
        width: ${actualNoteWidth}px;
        font-family: Roboto;
        font-size: ${fontSize || 18}px;
        font-weight: bold;
        color: black;
      ">Note</div>
    `;

    // Note content - render with PROCESSED content (highlighting fixed) with natural height
    html += `
      <div style="
        position: absolute;
        top: ${yNoteContent}px;
        left: 10px;
        width: ${actualNoteWidth}px;
        max-width: ${actualNoteWidth}px;
        font-size: ${noteFontSize}px;
        font-family: ${noteFontFamily};
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: break-word;
        color: ${getTextStyle(props, "note").color || "#000"};
        text-decoration: none;
        text-rendering: geometricPrecision;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-kerning: normal;
        font-variant-ligatures: none;
        letter-spacing: normal;
        word-spacing: normal;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        display: block;
      ">${processedNoteContent || ""}</div>
    `;
  }

  return html;
};

// Generate HTML for lines
export const generateLineHTML = (props) => {
  const {
    points = [],
    x = 0,
    y = 0,
    stroke = "#000",
    strokeWidth = 2,
    strokeColor,
  } = props;

  if (points.length < 4) return "";

  // Handle stroke color - prefer strokeColor over stroke, and convert decimal to hex if needed
  const resolvedStroke = decimalToHex(strokeColor || stroke || "#000000");

  // Handle multiple points for bent lines
  if (points.length >= 6) {
    // Multi-point line (bent)
    return generateBentLineHTML({ ...props, stroke: resolvedStroke });
  } else {
    // Simple straight line
    // Convert relative points to absolute - points are relative to line position, but need offset for HTML
    const startX = x + 20;
    const startY = y + 40;
    const endX = startX + (points[2] || 0);
    const endY = startY + (points[3] || 0);

    const length = Math.sqrt(
      Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2)
    );
    const angle = (Math.atan2(endY - startY, endX - startX) * 180) / Math.PI;

    return `
      <div style="
        position: absolute;
        left: ${startX}px;
        top: ${startY}px;
        width: ${length}px;
        height: ${strokeWidth}px;
        background-color: ${resolvedStroke};
        transform-origin: 0 50%;
        transform: rotate(${angle}deg);
        z-index: 2;
      "></div>
    `;
  }
};

// Generate HTML for bent lines with multiple points
export const generateBentLineHTML = (props) => {
  const { points = [], x = 0, y = 0, stroke = "#000", strokeWidth = 2 } = props;

  if (points.length < 6) return "";

  let html = "";

  // Re-normalize line points to ensure consistency (same logic as bent arrows)
  // Step 1: Calculate the bounding box of the current points
  let pointsMinX = Infinity,
    pointsMinY = Infinity;
  let pointsMaxX = -Infinity,
    pointsMaxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    const px = points[i] || 0;
    const py = points[i + 1] || 0;
    pointsMinX = Math.min(pointsMinX, px);
    pointsMaxX = Math.max(pointsMaxX, px);
    pointsMinY = Math.min(pointsMinY, py);
    pointsMaxY = Math.max(pointsMaxY, py);
  }

  // Step 2: Check if points need re-normalization
  const needsNormalization =
    Math.abs(pointsMinX) > 5 || Math.abs(pointsMinY) > 5;

  // Step 3: Normalize points to be relative to their bounding box origin
  let normalizedPoints = points;
  if (needsNormalization) {
    normalizedPoints = points.map((coord, i) =>
      i % 2 === 0 ? coord - pointsMinX : coord - pointsMinY
    );
  }

  // Step 4: Convert normalized relative points to absolute screen coordinates
  const baseX = needsNormalization ? x + pointsMinX + 20 : x + 20;
  const baseY = needsNormalization ? y + pointsMinY + 40 : y + 40;

  const absolutePoints = [];
  for (let i = 0; i < normalizedPoints.length; i += 2) {
    if (i + 1 < normalizedPoints.length) {
      const absX = baseX + (normalizedPoints[i] || 0);
      const absY = baseY + (normalizedPoints[i + 1] || 0);
      absolutePoints.push({ x: absX, y: absY });
    }
  }

  // Draw line segments using SVG polyline for precise positioning
  const linePoints = absolutePoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  html += `
    <svg style="
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
      pointer-events: none;
    ">
      <polyline 
        points="${linePoints}" 
        fill="none" 
        stroke="${stroke}" 
        stroke-width="${strokeWidth}"
      />
    </svg>
  `;

  return html;
};

// Generate HTML for arrows
export const generateArrowHTML = (props, type) => {
  const {
    points = [],
    x = 0,
    y = 0,
    stroke = "#000",
    strokeWidth = 2,
    strokeColor,
  } = props;

  if (points.length < 4) return "";

  // Handle stroke color - prefer strokeColor over stroke, and convert decimal to hex if needed
  const resolvedStroke = decimalToHex(strokeColor || stroke || "#000000");

  // Handle multiple points for bent arrows
  if (points.length >= 6) {
    // Multi-point arrow (bent)
    return generateBentArrowHTML({ ...props, stroke: resolvedStroke }, type);
  } else {
    // Simple straight arrow
    return generateStraightArrowHTML(
      { ...props, stroke: resolvedStroke },
      type
    );
  }
};

// Generate HTML for bent arrows with multiple points
export const generateBentArrowHTML = (props, type) => {
  const { points = [], x = 0, y = 0, stroke = "#000", strokeWidth = 2 } = props;

  if (points.length < 6) return "";

  let html = "";

  // CRITICAL FIX: Always re-normalize arrow points to ensure consistency
  // After waypoint dragging, points should be relative to (x, y), but we re-normalize to be safe

  // Step 1: Calculate the bounding box of the current points to find their actual position
  let pointsMinX = Infinity,
    pointsMinY = Infinity;
  let pointsMaxX = -Infinity,
    pointsMaxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    const px = points[i] || 0;
    const py = points[i + 1] || 0;
    pointsMinX = Math.min(pointsMinX, px);
    pointsMaxX = Math.max(pointsMaxX, px);
    pointsMinY = Math.min(pointsMinY, py);
    pointsMaxY = Math.max(pointsMaxY, py);
  }

  // Step 2: Check if points need re-normalization
  // Points should start from near (0,0) if they're properly relative to (x, y)
  // If pointsMinX or pointsMinY are far from 0, points might be absolute or improperly normalized
  const needsNormalization =
    Math.abs(pointsMinX) > 5 || Math.abs(pointsMinY) > 5;

  // Step 3: Normalize points to be relative to their bounding box origin
  let normalizedPoints = points;
  if (needsNormalization) {
    // Re-normalize: make points relative to their own bounding box
    normalizedPoints = points.map((coord, i) =>
      i % 2 === 0 ? coord - pointsMinX : coord - pointsMinY
    );
  }

  // Step 4: Convert normalized relative points to absolute screen coordinates
  // Use the arrow's position (x, y) as the base, adding container offsets (20, 40)
  const baseX = needsNormalization ? x + pointsMinX + 20 : x + 20;
  const baseY = needsNormalization ? y + pointsMinY + 40 : y + 40;

  const absolutePoints = [];
  for (let i = 0; i < normalizedPoints.length; i += 2) {
    if (i + 1 < normalizedPoints.length) {
      const absX = baseX + (normalizedPoints[i] || 0);
      const absY = baseY + (normalizedPoints[i + 1] || 0);
      absolutePoints.push({ x: absX, y: absY });
    }
  }

  // Draw line segments using SVG for precise positioning
  const linePoints = absolutePoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  html += `
    <svg style="
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
      pointer-events: none;
    ">
      <polyline 
        points="${linePoints}" 
        fill="none" 
        stroke="${stroke}" 
        stroke-width="${strokeWidth}"
      />
    </svg>
  `;

  // Add arrowhead at the end
  if (absolutePoints.length > 1) {
    const lastPoint = absolutePoints[absolutePoints.length - 1];
    const secondLastPoint = absolutePoints[absolutePoints.length - 2];

    const angle =
      (Math.atan2(
        lastPoint.y - secondLastPoint.y,
        lastPoint.x - secondLastPoint.x
      ) *
        180) /
      Math.PI;

    // Calculate proper arrowhead position - align with line edge accounting for stroke width
    const arrowheadSize = 10; // Moderate arrowhead length
    const arrowheadHeight = 10; // Balanced width for less pointed appearance
    const arrowheadPullback = 6; // Pull arrowhead back towards the line

    // Position the arrowhead base pulled back from the endpoint
    const arrowheadBaseX =
      lastPoint.x - Math.cos((angle * Math.PI) / 180) * arrowheadPullback;
    const arrowheadBaseY =
      lastPoint.y - Math.sin((angle * Math.PI) / 180) * arrowheadPullback;

    // Calculate the three points of the triangle for a balanced arrowhead
    const tipX =
      arrowheadBaseX + Math.cos((angle * Math.PI) / 180) * arrowheadSize;
    const tipY =
      arrowheadBaseY + Math.sin((angle * Math.PI) / 180) * arrowheadSize;

    const leftX =
      arrowheadBaseX +
      Math.cos(((angle - 120) * Math.PI) / 180) * (arrowheadHeight / 2);
    const leftY =
      arrowheadBaseY +
      Math.sin(((angle - 120) * Math.PI) / 180) * (arrowheadHeight / 2);

    const rightX =
      arrowheadBaseX +
      Math.cos(((angle + 120) * Math.PI) / 180) * (arrowheadHeight / 2);
    const rightY =
      arrowheadBaseY +
      Math.sin(((angle + 120) * Math.PI) / 180) * (arrowheadHeight / 2);

    html += `
      <svg style="
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 2;
        pointer-events: none;
      ">
        <polygon 
          points="${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}" 
          fill="${stroke}" 
          stroke="none"
        />
      </svg>
    `;
  }

  // Add start arrowhead for double-sided arrows
  if (type === "double_sided_arrow" && absolutePoints.length > 1) {
    const firstPoint = absolutePoints[0];
    const secondPoint = absolutePoints[1];

    const angle =
      (Math.atan2(secondPoint.y - firstPoint.y, secondPoint.x - firstPoint.x) *
        180) /
      Math.PI;

    // Calculate proper arrowhead position - align with line edge accounting for stroke width
    const arrowheadSize = 10; // Moderate arrowhead length
    const arrowheadHeight = 10; // Balanced width for less pointed appearance
    const arrowheadPullback = 6; // Pull arrowhead back towards the line
    const reverseAngle = angle + 180;

    // Position the arrowhead base pulled back from the start point
    const arrowheadBaseX =
      firstPoint.x -
      Math.cos((reverseAngle * Math.PI) / 180) * arrowheadPullback;
    const arrowheadBaseY =
      firstPoint.y -
      Math.sin((reverseAngle * Math.PI) / 180) * arrowheadPullback;

    // Calculate the three points of the triangle for a balanced arrowhead
    const tipX =
      arrowheadBaseX + Math.cos((reverseAngle * Math.PI) / 180) * arrowheadSize;
    const tipY =
      arrowheadBaseY + Math.sin((reverseAngle * Math.PI) / 180) * arrowheadSize;

    const leftX =
      arrowheadBaseX +
      Math.cos(((reverseAngle - 120) * Math.PI) / 180) * (arrowheadHeight / 2);
    const leftY =
      arrowheadBaseY +
      Math.sin(((reverseAngle - 120) * Math.PI) / 180) * (arrowheadHeight / 2);

    const rightX =
      arrowheadBaseX +
      Math.cos(((reverseAngle + 120) * Math.PI) / 180) * (arrowheadHeight / 2);
    const rightY =
      arrowheadBaseY +
      Math.sin(((reverseAngle + 120) * Math.PI) / 180) * (arrowheadHeight / 2);

    html += `
      <svg style="
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 2;
        pointer-events: none;
      ">
        <polygon 
          points="${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}" 
          fill="${stroke}" 
          stroke="none"
        />
      </svg>
    `;
  }

  return html;
};

// Generate HTML for simple straight arrows
export const generateStraightArrowHTML = (props, type) => {
  const { points = [], x = 0, y = 0, stroke = "#000", strokeWidth = 2 } = props;

  if (points.length < 4) return "";

  // CRITICAL FIX: Always re-normalize arrow points to ensure consistency
  // After waypoint dragging, points should be relative to (x, y), but we re-normalize to be safe

  // Step 1: Calculate the bounding box of the current points
  let pointsMinX = Math.min(points[0] || 0, points[2] || 0);
  let pointsMinY = Math.min(points[1] || 0, points[3] || 0);

  // Step 2: Check if points need re-normalization
  // Points should start from near (0,0) if they're properly relative to (x, y)
  const needsNormalization =
    Math.abs(pointsMinX) > 5 || Math.abs(pointsMinY) > 5;

  // Step 3: Normalize points to be relative to their bounding box origin
  let normalizedPoints = points;
  if (needsNormalization) {
    normalizedPoints = [
      points[0] - pointsMinX,
      points[1] - pointsMinY,
      points[2] - pointsMinX,
      points[3] - pointsMinY,
    ];
  }

  // Step 4: Convert normalized relative points to absolute screen coordinates
  // Use the arrow's position (x, y) as the base, adding container offsets (20, 40)
  const baseX = needsNormalization ? x + pointsMinX + 20 : x + 20;
  const baseY = needsNormalization ? y + pointsMinY + 40 : y + 40;

  const startX = baseX + (normalizedPoints[0] || 0);
  const startY = baseY + (normalizedPoints[1] || 0);
  const endX = baseX + (normalizedPoints[2] || 0);
  const endY = baseY + (normalizedPoints[3] || 0);

  const length = Math.sqrt(
    Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2)
  );
  const angle = (Math.atan2(endY - startY, endX - startX) * 180) / Math.PI;

  // Create arrow using SVG for precise positioning
  const arrowHTML = `
    <svg style="
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
      pointer-events: none;
    ">
      <line 
        x1="${startX}" 
        y1="${startY}" 
        x2="${endX}" 
        y2="${endY}" 
        stroke="${stroke}" 
        stroke-width="${strokeWidth}"
      />
    </svg>
  `;

  // Add arrowhead
  const arrowheadSize = 10; // Moderate arrowhead length
  const arrowheadHeight = 10; // Balanced width for less pointed appearance
  const arrowheadPullback = 6; // Pull arrowhead back towards the line

  // Position the arrowhead base pulled back from the endpoint
  const arrowheadBaseX =
    endX - Math.cos((angle * Math.PI) / 180) * arrowheadPullback;
  const arrowheadBaseY =
    endY - Math.sin((angle * Math.PI) / 180) * arrowheadPullback;

  // Calculate the three points of the triangle for a balanced arrowhead
  const tipX =
    arrowheadBaseX + Math.cos((angle * Math.PI) / 180) * arrowheadSize;
  const tipY =
    arrowheadBaseY + Math.sin((angle * Math.PI) / 180) * arrowheadSize;

  const leftX =
    arrowheadBaseX +
    Math.cos(((angle - 120) * Math.PI) / 180) * (arrowheadHeight / 2);
  const leftY =
    arrowheadBaseY +
    Math.sin(((angle - 120) * Math.PI) / 180) * (arrowheadHeight / 2);

  const rightX =
    arrowheadBaseX +
    Math.cos(((angle + 120) * Math.PI) / 180) * (arrowheadHeight / 2);
  const rightY =
    arrowheadBaseY +
    Math.sin(((angle + 120) * Math.PI) / 180) * (arrowheadHeight / 2);

  const arrowheadHTML = `
    <svg style="
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
      pointer-events: none;
    ">
      <polygon 
        points="${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}" 
        fill="${stroke}" 
        stroke="none"
      />
    </svg>
  `;

  // Add start arrowhead for double-sided arrows
  const startArrowheadHTML =
    type === "double_sided_arrow"
      ? (() => {
          const reverseAngle = angle + 180;

          // Position the arrowhead base pulled back from the start point
          const startArrowheadBaseX =
            startX -
            Math.cos((reverseAngle * Math.PI) / 180) * arrowheadPullback;
          const startArrowheadBaseY =
            startY -
            Math.sin((reverseAngle * Math.PI) / 180) * arrowheadPullback;

          // Calculate the three points of the triangle for a balanced arrowhead
          const startTipX =
            startArrowheadBaseX +
            Math.cos((reverseAngle * Math.PI) / 180) * arrowheadSize;
          const startTipY =
            startArrowheadBaseY +
            Math.sin((reverseAngle * Math.PI) / 180) * arrowheadSize;

          const startLeftX =
            startArrowheadBaseX +
            Math.cos(((reverseAngle - 120) * Math.PI) / 180) *
              (arrowheadHeight / 2);
          const startLeftY =
            startArrowheadBaseY +
            Math.sin(((reverseAngle - 120) * Math.PI) / 180) *
              (arrowheadHeight / 2);

          const startRightX =
            startArrowheadBaseX +
            Math.cos(((reverseAngle + 120) * Math.PI) / 180) *
              (arrowheadHeight / 2);
          const startRightY =
            startArrowheadBaseY +
            Math.sin(((reverseAngle + 120) * Math.PI) / 180) *
              (arrowheadHeight / 2);

          return `
    <svg style="
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2;
      pointer-events: none;
    ">
      <polygon 
        points="${startTipX},${startTipY} ${startLeftX},${startLeftY} ${startRightX},${startRightY}" 
        fill="${stroke}" 
        stroke="none"
      />
    </svg>
  `;
        })()
      : "";

  return arrowHTML + arrowheadHTML + startArrowheadHTML;
};

// Helper function to check if content contains lists
const hasListContent = (htmlContent) => {
  if (!htmlContent) return false;
  return (
    htmlContent.includes("<ul>") ||
    htmlContent.includes("<ol>") ||
    htmlContent.includes("<li>") ||
    htmlContent.includes("<ul ") ||
    htmlContent.includes("<ol ") ||
    htmlContent.includes("<li ")
  );
};

// Helper function to process HTML content with proper highlighting for wrapped text
// underlineLinks: whether to add underline to anchor tags (default: true)
export const processHtmlContentForHighlighting = (
  htmlContent,
  underlineLinks = true
) => {
  if (!htmlContent) return "";

  // Create a temporary container to process the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  // Helper function to check if node is inside an anchor tag
  const isInsideAnchor = (node) => {
    let parent = node.parentElement;
    while (parent && parent !== tempDiv) {
      if (parent.tagName && parent.tagName.toLowerCase() === "a") {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  };

  // Process the HTML to fix highlighting spans for wrapped text
  const processNode = (node, insideAnchor = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();

      // Handle anchor tags (links) FIRST - process before breaking into spans
      // This ensures link structure is preserved
      if (tagName === "a") {
        const href = node.getAttribute("href") || "";
        const target = node.getAttribute("target") || "";
        const rel = node.getAttribute("rel") || "";

        let attributes = `href="${href}"`;
        if (target) attributes += ` target="${target}"`;
        if (rel) attributes += ` rel="${rel}"`;

        // Preserve existing inline styles (especially color)
        const existingStyle = node.getAttribute("style") || "";

        // Build link style - conditionally add underline based on underlineLinks parameter
        let linkStyle = underlineLinks
          ? "text-decoration: underline; text-decoration-color: #4da6ff; display: inline; white-space: normal;"
          : "text-decoration: none; display: inline; white-space: normal;";

        // Append existing styles to preserve custom colors and other formatting
        if (existingStyle) {
          // Remove any conflicting text-decoration from existing styles
          const cleanedExistingStyle = existingStyle
            .replace(/text-decoration:[^;]*;?/gi, "")
            .trim();
          if (cleanedExistingStyle) {
            linkStyle += ` ${cleanedExistingStyle}`;
          }
        }

        // Extract color from child spans if present (for custom colored links)
        const firstChildSpan = Array.from(node.childNodes).find(
          (child) =>
            child.nodeType === Node.ELEMENT_NODE &&
            child.tagName?.toLowerCase() === "span"
        );
        if (
          firstChildSpan &&
          firstChildSpan.style.color &&
          !existingStyle.includes("color:")
        ) {
          linkStyle += ` color: ${firstChildSpan.style.color};`;
        }

        attributes += ` style="${linkStyle}"`;

        // Process inner content, passing flag that we're inside an anchor
        const innerContent = Array.from(node.childNodes)
          .map((child) => processNode(child, true))
          .join("");

        // Extract plain text from HTML for debugging
        const plainTextContent = innerContent.replace(/<[^>]*>/g, "").trim();

        return `<a ${attributes}>${innerContent}</a>`;
      }

      // Handle highlighting spans - break them into word-level spans to prevent full-width highlighting
      if (tagName === "span") {
        const style = node.style;

        // Check if this span has highlighting
        if (style.backgroundColor && style.backgroundColor !== "transparent") {
          // Check if we're inside a list item - if so, handle differently to preserve numbering
          let isInsideListItem = false;
          let parent = node.parentElement;
          while (parent && parent !== tempDiv) {
            if (parent.tagName && parent.tagName.toLowerCase() === "li") {
              isInsideListItem = true;
              break;
            }
            parent = parent.parentElement;
          }

          // This is a highlighted span - break it into word-level spans
          const innerContent = Array.from(node.childNodes)
            .map((child) => processNode(child, insideAnchor))
            .join("");

          // Split the content into words and wrap each word in its own span
          // We need to preserve highlighting for whitespace between highlighted words
          const words = innerContent.split(/(\s+)/); // Split on whitespace but keep the whitespace

          // First pass: identify which words should be highlighted and build the spans
          const processedWords = words.map((word, index) => {
            if (word.trim() === "") {
              return { word, isWhitespace: true, shouldHighlight: false };
            } else {
              // This is a word - it should be highlighted since it's inside a highlighted span
              return { word, isWhitespace: false, shouldHighlight: true };
            }
          });

          // Second pass: determine which whitespace should be highlighted
          // Whitespace between two highlighted words should be highlighted
          for (let i = 0; i < processedWords.length; i++) {
            if (
              processedWords[i].isWhitespace &&
              processedWords[i].shouldHighlight === false
            ) {
              const prevWord = i > 0 ? processedWords[i - 1] : null;
              const nextWord =
                i < processedWords.length - 1 ? processedWords[i + 1] : null;

              // If both previous and next words are highlighted (not whitespace), highlight this whitespace
              if (
                prevWord &&
                nextWord &&
                !prevWord.isWhitespace &&
                prevWord.shouldHighlight &&
                !nextWord.isWhitespace &&
                nextWord.shouldHighlight
              ) {
                processedWords[i].shouldHighlight = true;
              }
            }
          }

          // Third pass: generate the HTML
          const highlightedSpans = processedWords.map((item) => {
            if (item.isWhitespace && item.shouldHighlight) {
              // Create a span for highlighted whitespace
              let wordStyle = "display: inline;";
              wordStyle += `background-color: ${style.backgroundColor};`;
              wordStyle += `padding: 1px 0px;`; // Vertical padding to match word spans
              wordStyle += `margin: 0px;`; // No margin
              wordStyle += `border: none;`; // No border
              wordStyle += `line-height: 1.2;`; // Match line height to fit within bounds
              wordStyle += `box-decoration-break: clone;`; // Ensure highlighting continues across line breaks
              wordStyle += `-webkit-box-decoration-break: clone;`; // Safari support

              // If inside anchor, conditionally add underline based on underlineLinks parameter
              if (insideAnchor && underlineLinks) {
                wordStyle += `text-decoration: underline; text-decoration-color: #4da6ff;`;
              } else if (insideAnchor && !underlineLinks) {
                wordStyle += `text-decoration: none;`;
              }

              return `<span style="${wordStyle}">${item.word}</span>`;
            } else if (!item.isWhitespace && item.shouldHighlight) {
              // Create a span for each highlighted word
              let wordStyle = "display: inline;";
              wordStyle += `background-color: ${style.backgroundColor};`;
              wordStyle += `padding: 1px 0.5px;`; // Balanced padding for coverage without overflow
              wordStyle += `border-radius: 2px;`;
              wordStyle += `line-height: 1.2;`; // Slightly reduced line height to fit within bounds
              wordStyle += `box-decoration-break: clone;`; // Ensure highlighting continues across line breaks
              wordStyle += `-webkit-box-decoration-break: clone;`; // Safari support

              // If inside anchor, conditionally add underline based on underlineLinks parameter
              if (insideAnchor && underlineLinks) {
                wordStyle += `text-decoration: underline; text-decoration-color: #4da6ff;`;
              } else if (insideAnchor && !underlineLinks) {
                wordStyle += `text-decoration: none;`;
              }

              if (style.color) {
                wordStyle += `color: ${style.color};`;
              }
              if (style.fontWeight) {
                wordStyle += `font-weight: ${style.fontWeight};`;
              }
              if (style.fontStyle) {
                wordStyle += `font-style: ${style.fontStyle};`;
              }

              return `<span style="${wordStyle}">${item.word}</span>`;
            } else {
              // Return unhighlighted content as-is
              return item.word;
            }
          });

          return highlightedSpans.join("");
        } else {
          // Non-highlighted span - process normally
          let spanStyle = "display: inline;";

          // If inside anchor, conditionally add underline based on underlineLinks parameter
          if (insideAnchor && underlineLinks) {
            spanStyle += `text-decoration: underline; text-decoration-color: #4da6ff;`;
          } else if (insideAnchor && !underlineLinks) {
            spanStyle += `text-decoration: none;`;
          }

          if (style.color) {
            spanStyle += `color: ${style.color};`;
          }
          if (style.fontWeight) {
            spanStyle += `font-weight: ${style.fontWeight};`;
          }
          if (style.fontStyle) {
            spanStyle += `font-style: ${style.fontStyle};`;
          }

          const innerContent = Array.from(node.childNodes)
            .map((child) => processNode(child, insideAnchor))
            .join("");

          return `<span style="${spanStyle}">${innerContent}</span>`;
        }
      }

      // Handle line breaks - ensure they don't break highlighting spans
      if (tagName === "br") {
        return "<br>";
      }

      // Handle paragraph and div tags - add line breaks but preserve content
      // Skip adding <br> for paragraphs/divs inside list items to prevent extra spacing
      if (tagName === "p" || tagName === "div") {
        const innerContent = Array.from(node.childNodes)
          .map((child) => processNode(child, insideAnchor))
          .join("");

        // Check if this paragraph/div is inside a list item
        let isInsideListItem = false;
        let parent = node.parentElement;
        while (parent) {
          if (parent.tagName && parent.tagName.toLowerCase() === "li") {
            isInsideListItem = true;
            break;
          }
          parent = parent.parentElement;
        }

        // Only add <br> if not inside a list item and if the content is not empty
        // Also, don't add <br> if this is the first element to prevent extra indentation
        if (isInsideListItem || !innerContent.trim()) {
          return innerContent;
        }

        // Check if this is the first paragraph/div in the content
        const isFirstElement = node === tempDiv.firstElementChild;
        return isFirstElement ? innerContent : innerContent + "<br>";
      }

      // Handle other formatting tags
      if (tagName === "b" || tagName === "strong") {
        const innerContent = Array.from(node.childNodes)
          .map((child) => processNode(child, insideAnchor))
          .join("");
        return `<strong>${innerContent}</strong>`;
      }

      if (tagName === "i" || tagName === "em") {
        const innerContent = Array.from(node.childNodes)
          .map((child) => processNode(child, insideAnchor))
          .join("");
        return `<em>${innerContent}</em>`;
      }

      // Handle lists - preserve structure and ensure proper numbering
      if (tagName === "ul" || tagName === "ol") {
        const items = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const itemContent = Array.from(li.childNodes)
              .map((child) => processNode(child, insideAnchor))
              .join("");
            return `<li style="display: list-item;">${itemContent}</li>`;
          })
          .join("");

        // Add inline styles to ensure list-style-type is visible
        const listStyle =
          tagName === "ul"
            ? "margin: 0; padding: 0 0 0 20px; list-style-type: disc; list-style-position: outside;"
            : "margin: 0; padding: 0 0 0 20px; list-style-type: decimal; list-style-position: outside;";

        return `<${tagName} style="${listStyle}">${items}</${tagName}>`;
      }

      if (tagName === "li") {
        const innerContent = Array.from(node.childNodes)
          .map((child) => processNode(child, insideAnchor))
          .join("");
        return `<li style="display: list-item;">${innerContent}</li>`;
      }

      // For other elements, process their children
      return Array.from(node.childNodes)
        .map((child) => processNode(child, insideAnchor))
        .join("");
    }

    return "";
  };

  // Process all child nodes of the container, not just the first one
  const processedContent = Array.from(tempDiv.childNodes)
    .map(processNode)
    .join("");

  return processedContent.trim().replace(/^\s+/, ""); // Remove any leading whitespace
};

// Helper function to wait for images to load
export const waitForImages = (container) => {
  const images = container.querySelectorAll("img");
  const promises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  });
  return Promise.all(promises);
};
